import React from 'react';

import {
  ErrorCode,
  type DropEvent,
  type FileRejection,
  type FileWithPath,
} from 'react-dropzone';

import { useNumbro } from '@utils/hooks';

import { alert } from '../../Alert';
import { acceptPropertyAsAcceptAttribute } from '../helpers';

import { DropZoneAreaView } from './DropZoneAreaView';
import {
  defaultFileLimitExceedMessage,
  defaultGetDropRejectMessage,
} from './messages';

import type { DropZoneBaseProperties } from './DropZoneAreaBase.types';

export const DropZoneAreaBase: React.FC<DropZoneBaseProperties> = ({
  dataTest = 'dropzone_area_base',
  accept = {},
  filesLimit: filesLimitProperty = 1,
  icon,
  filesCount = 0,
  maxFileSize = 30_000_000,
  dropzoneText = 'Перетащите файлы сюда или нажмите, чтобы выбрать файлы',
  getFileLimitExceedMessage = defaultFileLimitExceedMessage,
  getDropRejectMessage = defaultGetDropRejectMessage,
  onAdd,
  onDrop,
  onDropRejected,
  inputProps,
  dropzoneProps,
  dropZoneStyles = [],
  dropzoneParagraphStyles = [],
  acceptedFormatsText,
  rejectionErrorDataTest,
  limitErrorDataTest,
}) => {
  // Лимит меньше единицы не значит «без лимита»: `isMultiple` ниже всё равно даст
  // `multiple={false}`, и react-dropzone продолжит резать мультивыбор. Связного
  // безлимитного режима у компонента нет, поэтому такое значение считаем невалидным
  // и сводим к дефолтной единице — иначе проверка ниже блокировала бы любой файл.
  const filesLimit = Math.max(1, filesLimitProperty);
  const isMultiple = filesLimit > 1;
  const acceptFiles = acceptPropertyAsAcceptAttribute(accept);

  const numeral = useNumbro();

  const maxFileSizeString = React.useMemo(
    () =>
      numeral(maxFileSize).format({
        output: 'byte',
        base: 'binary',
        mantissa: 2,
        spaceSeparated: true,
      }),
    [maxFileSize, numeral],
  );

  const handleDropAccepted = async (
    acceptedFiles: FileWithPath[],
    event: DropEvent,
  ) => {
    if (filesCount + acceptedFiles.length > filesLimit) {
      alert.error(getFileLimitExceedMessage(filesLimit), limitErrorDataTest);
      return;
    }
    // Notify Drop event
    onDrop?.(acceptedFiles, event);
    // Notify added files
    onAdd?.(acceptedFiles);
  };

  const handleDropRejected = (
    rejectedFiles: FileRejection[],
    event: DropEvent,
  ) => {
    let message = '';
    let errorDataTest: string | undefined;
    // Про лимит говорим только если файлы отклонены именно из-за количества.
    // Считать это арифметикой нельзя: при полном списке любой отказ по типу или
    // размеру превращался в «превышен лимит», и пользователь чинил не ту проблему.
    const isLimitRejection = rejectedFiles.some(rejectedFile =>
      rejectedFile.errors.some(error => error.code === ErrorCode.TooManyFiles),
    );
    if (isLimitRejection) {
      message = getFileLimitExceedMessage(filesLimit);
      errorDataTest = limitErrorDataTest;
    } else {
      for (const rejectedFile of rejectedFiles) {
        message = getDropRejectMessage(rejectedFile, acceptFiles, maxFileSize);
      }
      errorDataTest = rejectionErrorDataTest;
    }
    onDropRejected?.(rejectedFiles, event);
    alert.error(message, errorDataTest);
  };

  return (
    <DropZoneAreaView
      dataTest={dataTest}
      dropzoneProps={dropzoneProps}
      accept={accept}
      handleDropAccepted={handleDropAccepted}
      handleDropRejected={handleDropRejected}
      maxFileSize={maxFileSize}
      maxFileSizeFormatted={maxFileSizeString}
      isMultiple={isMultiple}
      filesLimit={filesLimit}
      dropzoneText={dropzoneText}
      icon={icon}
      inputProps={inputProps}
      dropZoneStyles={dropZoneStyles}
      dropzoneParagraphStyles={dropzoneParagraphStyles}
      acceptedFormatsText={acceptedFormatsText}
    />
  );
};
